import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useTheme } from '@/contexts/ThemeContext'
import { orgApi, authApi, usersApi } from '@/api/clients/apiClient'
import { RoleBadge, PlanBadge } from '@/components/common/StatusBadge'
import type { OrgPlan } from '@/api/types'

const inputCls =
  'w-full px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50'

export default function Settings() {
  const { orgId, role, userId, refreshToken } = useAuthStore()
  const authStore = useAuthStore()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Org settings
  const [orgName, setOrgName] = useState('')
  const [orgPlan, setOrgPlan] = useState<OrgPlan>('free')
  const [orgSuccessMsg, setOrgSuccessMsg] = useState('')

  // Change password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwdSuccessMsg, setPwdSuccessMsg] = useState('')

  // Danger zone
  const [confirmDeleteInput, setConfirmDeleteInput] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: orgResp, isLoading } = useQuery({
    queryKey: ['org', orgId],
    queryFn: () => orgApi.get(orgId!),
    enabled: !!orgId,
  })

  // Populate form when org loads
  useEffect(() => {
    const org = orgResp?.data
    if (org) {
      setOrgName(org.name)
      setOrgPlan(org.plan)
    }
  }, [orgResp])

  const updateOrgMutation = useMutation({
    mutationFn: () => orgApi.update(orgId!, { name: orgName, plan: orgPlan }),
    onSuccess: () => {
      setOrgSuccessMsg('Organisation updated.')
      setTimeout(() => setOrgSuccessMsg(''), 3000)
      queryClient.invalidateQueries({ queryKey: ['org', orgId] })
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: () =>
      usersApi.changePassword(orgId!, userId!, {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPwdSuccessMsg('Password updated.')
      setTimeout(() => setPwdSuccessMsg(''), 3000)
    },
  })

  const deleteOrgMutation = useMutation({
    mutationFn: () => orgApi.delete(orgId!),
    onSuccess: () => {
      authStore.clearSession()
      navigate('/login')
    },
  })

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken)
      }
    } catch {
      // ignore
    }
    authStore.clearSession()
    navigate('/login')
  }

  const org = orgResp?.data

  const pwdMismatch = confirmPassword !== '' && confirmPassword !== newPassword
  const pwdTooShort = newPassword !== '' && newPassword.length < 8
  const pwdSubmitDisabled =
    !currentPassword || !newPassword || !confirmPassword ||
    pwdMismatch || pwdTooShort || changePasswordMutation.isPending

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Settings</h1>
        <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
          Manage your organisation and account preferences
        </p>
      </div>

      {/* Organization */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Organisation
        </h2>

        {orgSuccessMsg && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
            <span className="material-icons text-success text-sm">check_circle</span>
            <p className="text-sm text-green-700 dark:text-green-300">{orgSuccessMsg}</p>
          </div>
        )}

        {updateOrgMutation.isError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">
              {(updateOrgMutation.error as Error)?.message ?? 'Failed to update organisation.'}
            </p>
          </div>
        )}

        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            updateOrgMutation.mutate()
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Name
            </label>
            <input
              type="text"
              required
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Plan
            </label>
            <select
              value={orgPlan}
              onChange={(e) => setOrgPlan(e.target.value as OrgPlan)}
              className={inputCls}
            >
              {(['free', 'starter', 'pro', 'enterprise'] as OrgPlan[]).map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
            {org?.plan && (
              <div className="mt-1">
                <PlanBadge plan={org.plan} />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={updateOrgMutation.isPending || role === 'member'}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 text-sm font-medium"
          >
            {updateOrgMutation.isPending && (
              <span className="material-icons text-base animate-spin">sync</span>
            )}
            Save changes
          </button>
          {role === 'member' && (
            <p className="text-xs text-subtle-light dark:text-subtle-dark">
              Only owners and admins can edit organisation settings.
            </p>
          )}
        </form>
      </div>

      {/* Appearance */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Appearance
        </h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-text-light dark:text-text-dark">
              Theme: <span className="font-medium capitalize">{theme}</span>
            </p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">
              Saved to browser storage
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-4 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark hover:bg-background-light dark:hover:bg-background-dark"
          >
            <span className="material-icons text-base">
              {theme === 'light' ? 'dark_mode' : 'light_mode'}
            </span>
            Switch to {theme === 'light' ? 'dark' : 'light'}
          </button>
        </div>
      </div>

      {/* Account */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Account
        </h2>
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-subtle-light dark:text-subtle-dark">
            <span className="material-icons text-base">badge</span>
            User ID: <span className="font-mono text-xs">{userId}</span>
          </div>
          {role && (
            <div className="flex items-center gap-2">
              <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
                manage_accounts
              </span>
              <span className="text-sm text-subtle-light dark:text-subtle-dark">Role:</span>
              <RoleBadge role={role} />
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark hover:border-error hover:text-error hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
        >
          <span className="material-icons text-base">logout</span>
          Sign out
        </button>
      </div>

      {/* Change Password */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Change Password
        </h2>

        {pwdSuccessMsg && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
            <span className="material-icons text-success text-sm">check_circle</span>
            <p className="text-sm text-green-700 dark:text-green-300">{pwdSuccessMsg}</p>
          </div>
        )}

        {changePasswordMutation.isError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">
              {(changePasswordMutation.error as Error)?.message ?? 'Failed to change password.'}
            </p>
          </div>
        )}

        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            changePasswordMutation.mutate()
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Current password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              New password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputCls}
            />
            {pwdTooShort && (
              <p className="text-xs text-error mt-1">Must be at least 8 characters.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputCls}
            />
            {pwdMismatch && (
              <p className="text-xs text-error mt-1">Passwords do not match.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={pwdSubmitDisabled}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 text-sm font-medium"
          >
            {changePasswordMutation.isPending && (
              <span className="material-icons text-base animate-spin">sync</span>
            )}
            Update password
          </button>
        </form>
      </div>

      {/* About */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">About</h2>
        <div className="space-y-2 text-sm text-subtle-light dark:text-subtle-dark">
          <p>
            <strong className="text-text-light dark:text-text-dark">Quantizant Account Portal</strong>{' '}
            — v{__APP_VERSION__}
          </p>
          <p>Frontend for the Quantizant PQC Transition Platform account module.</p>
          <p>
            Manage your organisation, team members, API keys, usage, and billing settings.
          </p>
        </div>
      </div>

      {/* Danger zone — owner only */}
      {role === 'owner' && (
        <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-error/40 p-6">
          <h2 className="text-base font-semibold text-error mb-2">Danger Zone</h2>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mb-4">
            Deleting the organisation is irreversible. All data will be lost.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 border border-error text-error rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium"
            >
              <span className="material-icons text-base">delete_forever</span>
              Delete organisation
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-text-light dark:text-text-dark">
                Type <strong className="font-mono">{org?.name}</strong> to confirm:
              </p>
              <input
                type="text"
                value={confirmDeleteInput}
                onChange={(e) => setConfirmDeleteInput(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-error/50 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-error/50"
                placeholder={org?.name}
              />
              {deleteOrgMutation.isError && (
                <p className="text-sm text-error">
                  {(deleteOrgMutation.error as Error)?.message ?? 'Delete failed.'}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => deleteOrgMutation.mutate()}
                  disabled={confirmDeleteInput !== org?.name || deleteOrgMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-error text-white rounded-lg hover:bg-red-600 disabled:opacity-40 text-sm font-medium"
                >
                  {deleteOrgMutation.isPending && (
                    <span className="material-icons text-base animate-spin">sync</span>
                  )}
                  Delete permanently
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setConfirmDeleteInput('')
                  }}
                  className="px-4 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark hover:bg-background-light dark:hover:bg-background-dark"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
